"use client";

import React from "react";
import { reportReactError } from "@/lib/error-reporting";

type Props = { children: React.ReactNode };
type State = { hasError: boolean; eventId: string | null };

export default class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, eventId: null };

  static getDerivedStateFromError(): State {
    return { hasError: true, eventId: Math.random().toString(36).slice(2, 10) };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    reportReactError(error, info);
  }

  private handleReload = () => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  private handleGoHome = () => {
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <div
        role="alert"
        style={{
          padding: "48px 24px",
          maxWidth: 480,
          margin: "64px auto",
          textAlign: "center",
          background: "var(--color-bg, #ffffff)",
          color: "var(--color-ink, #0a0a0a)",
          borderRadius: 12,
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 12px 32px rgba(0,0,0,0.06)",
        }}
      >
        <h1 style={{ fontSize: 22, margin: 0, marginBottom: 12 }}>Something broke on this page.</h1>
        <p style={{ margin: "8px 0 24px", color: "rgba(0,0,0,0.65)" }}>
          The error was reported automatically. You can reload, or go back home.
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.12)",
              background: "var(--color-ink, #0a0a0a)",
              color: "#fff",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Reload page
          </button>
          <button
            type="button"
            onClick={this.handleGoHome}
            style={{
              padding: "10px 18px",
              borderRadius: 8,
              border: "1px solid rgba(0,0,0,0.16)",
              background: "transparent",
              color: "inherit",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Go home
          </button>
        </div>
        {this.state.eventId && (
          <p style={{ marginTop: 24, fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
            Reference: {this.state.eventId}
          </p>
        )}
      </div>
    );
  }
}
